export type Color = 'white' | 'black';

export type StandardPieceType = 'king' | 'queen' | 'rook' | 'bishop' | 'knight' | 'pawn';

export type PieceType = StandardPieceType | (string & {});

export interface Piece {
	readonly type: PieceType;
	readonly color: Color;
	readonly hasMoved: boolean;
}

export interface Position {
	readonly row: number;
	readonly col: number;
}

export interface Move {
	readonly from: Position;
	readonly to: Position;
	readonly captured: Piece | null;
	readonly isEnPassant: boolean;
	readonly isCastle: boolean;
	readonly promotion: PieceType | null;
}

export type TurnPhase = 'move' | 'placeDuck';

export type GameStatus = 'playing' | 'check' | 'checkmate' | 'stalemate' | 'kingCaptured' | 'fowled';

export interface GameState {
	readonly board: Board;
	readonly currentTurn: Color;
	readonly selectedPosition: Position | null;
	readonly validMoves: readonly Move[];
	readonly moveHistory: readonly Move[];
	readonly boardSize: number;
	readonly gameStatus: GameStatus;
	readonly enPassantTarget: Position | null;
	readonly pendingPromotion: Move | null;
	readonly duckPosition: Position | null;
	readonly turnPhase: TurnPhase;
}

export class Board {
	readonly size: number;
	private readonly grid: ReadonlyArray<ReadonlyArray<Piece | null>>;

	constructor(grid: ReadonlyArray<ReadonlyArray<Piece | null>>) {
		this.size = grid.length;
		this.grid = grid;
	}

	get(row: number, col: number): Piece | null {
		return this.grid[row]![col] as Piece | null;
	}

	withPiece(row: number, col: number, piece: Piece | null): Board {
		const grid = this.grid.map((r) => [...r]);
		grid[row]![col] = piece;
		return new Board(grid);
	}

	toMutableGrid(): (Piece | null)[][] {
		return this.grid.map((r) => [...r]);
	}

	static empty(size: number): Board {
		return new Board(Array.from({length: size}, () => Array.from<Piece | null>({length: size}).fill(null)));
	}
}

export interface GameSettings {
	readonly boardSize: number;
	readonly enabledPieces: ReadonlySet<string>;
	readonly pieceSet: string;
	readonly boardTheme: string;
	readonly duckChess: boolean;
}
